import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["dialog", "input", "results"]
  static values = { url: String }

  connect() {
    this.selectedIndex = -1
    this.debounceTimer = null
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  handleKeydown(event) {
    // Cmd+K or Ctrl+K to open
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
    }
  }

  open() {
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.selectedIndex = -1
    this.clearResults()
  }

  close() {
    this.dialogTarget.close()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  // Called on input event
  search() {
    const query = this.inputTarget.value.trim()

    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    if (query.length === 0) {
      this.clearResults()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 200)
  }

  fetchResults(query) {
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    const frame = this.resultsTarget.querySelector("turbo-frame") || this.resultsTarget

    // Use Turbo to fetch the frame
    fetch(url, {
      headers: {
        "Turbo-Frame": "search_results",
        "Accept": "text/html"
      }
    })
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const newFrame = doc.querySelector("turbo-frame#search_results")
      if (newFrame) {
        const existingFrame = this.resultsTarget.querySelector("turbo-frame#search_results")
        if (existingFrame) {
          existingFrame.innerHTML = newFrame.innerHTML
        }
      }
      this.selectedIndex = -1
      this.updateSelection()
    })
  }

  clearResults() {
    const frame = this.resultsTarget.querySelector("turbo-frame#search_results")
    if (frame) frame.innerHTML = ""
  }

  // Keyboard navigation within results
  navigateResults(event) {
    const results = this.getResultLinks()
    if (results.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, results.length - 1)
        this.updateSelection()
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateSelection()
        if (this.selectedIndex === -1) this.inputTarget.focus()
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
          const url = results[this.selectedIndex].getAttribute("href")
          this.close()
          Turbo.visit(url)
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  handleResultClick(event) {
    const link = event.target.closest("a.search-result")
    if (!link) return
    event.preventDefault()
    const url = link.getAttribute("href")
    this.close()
    Turbo.visit(url)
  }

  getResultLinks() {
    return Array.from(this.resultsTarget.querySelectorAll("a.search-result"))
  }

  updateSelection() {
    const results = this.getResultLinks()
    results.forEach((el, i) => {
      if (i === this.selectedIndex) {
        el.classList.add("bg-base-200")
        el.scrollIntoView({ block: "nearest" })
      } else {
        el.classList.remove("bg-base-200")
      }
    })
  }
}
